import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';
import {
  School as CourseIcon,
  People as PeopleIcon,
  CheckCircle as CompletedIcon,
  TrendingUp as TrendingIcon,
  Analytics as AnalyticsIcon,
  Psychology as QualityIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { analyticsService, InstructorStats, CourseStats } from '../../services/analyticsService';
import StatsCard from './StatsCard';

const InstructorAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<InstructorStats | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [courseStats, setCourseStats] = useState<CourseStats | null>(null);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [courseError, setCourseError] = useState(false);

  useEffect(() => {
    loadInstructorStats();
  }, []);

  useEffect(() => {
    if (selectedCourseId) {
      loadCourseStats(selectedCourseId);
    } else {
      setCourseStats(null);
    }
  }, [selectedCourseId]);

  const loadInstructorStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await analyticsService.getInstructorStats();
      setStats(data);
      // Auto-select first course if available
      if (data.courses.length > 0 && !selectedCourseId) {
        setSelectedCourseId(data.courses[0].id);
      }
    } catch (err) {
      console.error('Error loading instructor stats:', err);
      setError(t('analytics.instructor.errorLoading', 'Fehler beim Laden der Trainer-Statistiken'));
    } finally {
      setLoading(false);
    }
  };

  const loadCourseStats = async (courseId: number) => {
    setLoadingCourse(true);
    setCourseError(false);
    try {
      const data = await analyticsService.getCourseStats(courseId);
      setCourseStats(data);
    } catch (err) {
      console.error('Error loading course stats:', err);
      setCourseStats(null);
      setCourseError(true);
    } finally {
      setLoadingCourse(false);
    }
  };

  const handleCourseChange = (_: React.MouseEvent<HTMLElement>, newCourseId: number | null) => {
    if (newCourseId !== null) {
      setSelectedCourseId(newCourseId);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return null;
  }

  if (stats.summary.totalCourses === 0) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <AnalyticsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {t('analytics.instructor.noCourses', 'Sie haben noch keine Kurse erstellt')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('analytics.instructor.createCourse', 'Erstellen Sie einen Kurs, um Statistiken zu sehen')}
        </Typography>
      </Box>
    );
  }

  const completionRate = stats.summary.totalEnrollments > 0
    ? Math.round((stats.summary.completedCount / stats.summary.totalEnrollments) * 100)
    : 0;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/analytics')}>
          Zurück zur Übersicht
        </Button>
      </Box>
      <Typography variant="h4" gutterBottom>
        {t('analytics.instructor.title', 'Trainer-Dashboard')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('analytics.instructor.subtitle', 'Übersicht über die Performance Ihrer Kurse')}
      </Typography>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('analytics.instructor.myCourses', 'Meine Kurse')}
            value={stats.summary.totalCourses}
            icon={<CourseIcon />}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('analytics.instructor.participants', 'Teilnehmer')}
            value={stats.summary.totalParticipants}
            icon={<PeopleIcon />}
            color="info"
            subtitle={`${stats.summary.totalEnrollments} ${t('analytics.instructor.enrollments', 'Einschreibungen')}`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('analytics.instructor.completions', 'Abschlüsse')}
            value={stats.summary.completedCount}
            icon={<CompletedIcon />}
            color="success"
            subtitle={`${completionRate}% ${t('analytics.instructor.completionRate', 'Abschlussrate')}`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title={t('analytics.instructor.avgProgress', 'Ø Fortschritt')}
            value={`${Math.round(stats.summary.avgProgress || 0)}%`}
            icon={<TrendingIcon />}
            color="warning"
            subtitle={stats.summary.avgScore !== null ? `Ø ${Math.round(stats.summary.avgScore)}% Score` : undefined}
          />
        </Grid>
      </Grid>

      {/* Course Overview */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('analytics.instructor.courseOverview', 'Kursübersicht')}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('analytics.instructor.course', 'Kurs')}</TableCell>
                  <TableCell align="center">{t('analytics.instructor.enrolled', 'Eingeschrieben')}</TableCell>
                  <TableCell align="center">{t('analytics.instructor.completed', 'Abgeschlossen')}</TableCell>
                  <TableCell align="center">{t('analytics.instructor.avgProgressShort', 'Ø Fortschritt')}</TableCell>
                  <TableCell align="center">{t('analytics.instructor.avgScore', 'Ø Punkte')}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <QualityIcon fontSize="small" />
                      KI-Qualität
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.courses.map((course) => (
                  <TableRow
                    key={course.id}
                    hover
                    selected={selectedCourseId === course.id}
                    onClick={() => setSelectedCourseId(course.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {course.title}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={course.enrollmentCount} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={course.completedCount}
                        size="small"
                        color={course.completedCount > 0 ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.round(course.avgProgress || 0)}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" sx={{ minWidth: 40 }}>
                          {Math.round(course.avgProgress || 0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {course.avgScore !== null ? (
                        <Chip
                          label={`${Math.round(course.avgScore)}%`}
                          size="small"
                          color={course.avgScore >= 80 ? 'success' : course.avgScore >= 50 ? 'primary' : 'warning'}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {course.qualityScore != null ? (
                        <Chip
                          label={`${course.qualityScore}/100`}
                          size="small"
                          color={course.qualityScore >= 75 ? 'success' : course.qualityScore >= 50 ? 'warning' : 'error'}
                          variant="outlined"
                          onClick={() => navigate(`/courses/${course.id}/quality`)}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Course Detail */}
      {selectedCourseId && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('analytics.instructor.lessonDetails', 'Lektionsdetails')}
              {stats.courses.find(c => c.id === selectedCourseId) && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  - {stats.courses.find(c => c.id === selectedCourseId)?.title}
                </Typography>
              )}
            </Typography>

            {loadingCourse ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : courseError ? (
              <Alert severity="error">
                {t('analytics.instructor.courseStatsError', 'Die Kursstatistik konnte nicht geladen werden')}
              </Alert>
            ) : courseStats ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>{t('analytics.instructor.lesson', 'Lektion')}</TableCell>
                      <TableCell align="center">{t('analytics.instructor.attempts', 'Versuche')}</TableCell>
                      <TableCell align="center">{t('analytics.instructor.lessonCompletions', 'Abschlüsse')}</TableCell>
                      <TableCell align="center">{t('analytics.instructor.completionRate', 'Abschlussrate')}</TableCell>
                      <TableCell align="center">{t('analytics.instructor.avgScore', 'Ø Punkte')}</TableCell>
                      <TableCell align="center">{t('analytics.instructor.avgTime', 'Ø Zeit')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {courseStats.lessons.map((lesson, index) => {
                      const lessonCompletionRate = lesson.attempts > 0
                        ? Math.round((lesson.completions / lesson.attempts) * 100)
                        : 0;
                      return (
                        <TableRow key={lesson.id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{lesson.title}</TableCell>
                          <TableCell align="center">{lesson.attempts}</TableCell>
                          <TableCell align="center">{lesson.completions}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${lessonCompletionRate}%`}
                              size="small"
                              color={lessonCompletionRate >= 80 ? 'success' : lessonCompletionRate >= 50 ? 'primary' : 'warning'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {lesson.avgScore !== null ? `${Math.round(lesson.avgScore)}%` : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {formatTime(lesson.avgTime)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {t('analytics.instructor.selectCourse', 'Wählen Sie einen Kurs aus der Tabelle')}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default InstructorAnalytics;
